import React, { useRef, useState } from 'react';
import {
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Box,
    Typography
} from '@mui/material';
import { styled } from '@mui/system';
import "./input.scss";
import { useFilePicker } from 'use-file-picker';
import {
    FileAmountLimitValidator,
    FileTypeValidator,
    FileSizeValidator,
    ImageDimensionsValidator,
} from 'use-file-picker/validators';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import JobAutocomplete from '../autocomplete/autocomplete';
import GlassCard from '../../shared-component/common/GlassCard';
import ButtonCustom from '../../shared-component/common/Button';
import * as Constants from '../../constants';
import { ChevronDownIcon, DocumentArrowUpIcon } from '../../shared-component/icons/Heroicons'; // Assuming you might add these or similar
const FileInput = styled('input')({
    display: 'none'
});

const InputForm = ({ submitFile }) => {
    const [jobTitle, setJobTitle] = useState<string>('');
    const [experience, setExperience] = useState<string>('');
    const [errorJobTitle, setErrorJobTitle] = useState<boolean>(false);
    const [errorExperience, setErrorExperience] = useState<boolean>(false);
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [errorFile, setIsErrorFile] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const experienceOptions = ['Entry Level', 'Junior Level', 'Mid Level', 'Senior Level', 'Lead Level', 'Manager Level', 'Director Level', 'Executive Level'];
    const { openFilePicker, filesContent, loading, errors } = useFilePicker({
        readAs: 'DataURL',
        accept: ['image/*', '.pdf'],
        multiple: false,
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(['jpg', 'png', 'pdf']),
        ],
        onFilesSelected: ({ plainFiles, filesContent, errors }) => {
            // this callback is always called, even if there are errors
            fileInputRef.current?.click();
            console.log('onFilesSelected', plainFiles, filesContent, errors);
        },
        onFilesRejected: ({ errors }) => {
            // this callback is called when there were validation errors
            console.log('onFilesRejected', errors);
        },
        onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
            // this callback is called when there were no validation errors
            setResumeFile(plainFiles[0])
            console.log('onFilesSuccessfullySelected', plainFiles, filesContent);
        },
    });

    const clearFile = () => {
        setResumeFile(null);
    }


    const handleSubmit = () => {

        // Handle form submission logic here
        if (jobTitle && experience && resumeFile) {
            const body = {
                jobTitle, experience, resumeFile
            }
            console.log(body);
            submitFile(body)
        } else {
            if (!jobTitle) {
                setErrorJobTitle(true);
            }
            if (!experience) {
                setErrorExperience(true);
            }

            if (!resumeFile) {
                setIsErrorFile(true);
            }
        }

    };

    const handleSelect = (value) => {
        if (value) {
            setErrorExperience(false);
            setExperience(value);
        }
    }

    const setJobTitleAutocomplete = (jobTitle) => {
        setJobTitle(jobTitle);
    }
    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setResumeFile(file);
        }
    };

    const onChangeTitle = () => {
        setErrorJobTitle(false);
    }
    return (
        <>


            <GlassCard className="w-full p-6 md:p-8 shadow-xl bg-white/90 border-slate-300/70 input-control">
                <h2 id="hero-form-title" className="text-2xl font-semibold text-slate-800 text-center mb-2">{Constants.HERO_FORM_TITLE}</h2>
                <p className="text-sm text-slate-600 text-center mb-6">{Constants.HERO_FORM_SUBTITLE}</p>

                <form onSubmit={(e) => e.preventDefault()}>
                    <div className={errorJobTitle ? 'input-form-control input-border-error' : 'input-form-control'}>
                          <p className="block text-sm font-bold text-slate-700 mb-1">
                            {Constants.HERO_FORM_JOB_POSITION_LABEL}
                        </p>
                        {/* <span className='input-form-title'>Job Title</span> */}

                        <JobAutocomplete errorJobTitle={errorJobTitle} onChangeTitle={onChangeTitle} setJobTitle={setJobTitleAutocomplete} />

                        {
                            errorJobTitle && <span className='input-form-error'>  {Constants.HERO_FORM_JOB_POSITION_PLACEHOLDER}</span>
                        }
                    </div>


                    <FormControl className={errorExperience ? 'input-form-control input-border-error' : 'input-form-control'}>
                          <p className="block text-sm font-bold text-slate-700 mb-1">
                            {Constants.HERO_FORM_EXP_LABEL}
                        </p>
                        {/* <span className='input-form-title'>Experience(s)</span> */}
                        <Select
                            value={experience}
                            onChange={(e) => handleSelect(e.target.value)}
                            placeholder='เลือกประสบการณ์'
                        >
                            <MenuItem value={0} selected>
                                <span className='input-placeholder-color'>เลือกประสบการณ์</span>
                            </MenuItem>
                            {experienceOptions.map((option, index) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                        {
                            errorExperience && <span className='input-form-error'>กรุณาเลือกประสบการณ์</span>
                        }
                    </FormControl>

                    <div className="mb-6">
                        <p className="block text-sm font-bold text-slate-700 mb-1">
                            {Constants.HERO_FORM_UPLOAD_RESUME_LABEL}
                        </p>
                        <div
                            onClick={handleUploadButtonClick}
                            className="mt-1 flex justify-center items-center px-6 pt-5 pb-6 border-2 border-purple-300 border-dashed rounded-md cursor-pointer hover:border-purple-500 bg-purple-50/30"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleUploadButtonClick()}
                        >
                            <div className="space-y-1 text-center">
                                <DocumentArrowUpIcon className="mx-auto h-10 w-10 text-purple-500" />
                                <div className="flex text-sm text-purple-700">
                                    <span className="font-semibold">{Constants.HERO_FORM_UPLOAD_RESUME_BUTTON_TEXT}</span>
                                    <input ref={fileInputRef} id="resume-upload" name="resume-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                                </div>
                                {resumeFile ? (
                                    <p className="text-xs text-slate-500">{resumeFile.name}</p>
                                ) : (
                                    <p className="text-xs text-slate-500">{Constants.HERO_FORM_FILE_UPLOAD_HELPER_TEXT}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="terms"
                                    name="terms"
                                    type="checkbox"
                                    className="focus:ring-purple-500 h-4 w-4 text-purple-600 border-slate-300 rounded"
                                />
                            </div>
                            <div className="ml-3 text-xs">
                                <label htmlFor="terms" className="text-slate-500">
                                    {Constants.HERO_FORM_TERMS_AGREEMENT_TEXT_PART1}{' '}
                                    <a href="https://scoutout.co/th/terms-th/" className="font-medium text-purple-600 hover:text-purple-800">
                                        {Constants.HERO_FORM_TERMS_LINK_TEXT}
                                    </a>{' '}
                                    และ{' '}
                                    <a href="https://scoutout.co/th/privacy-th/" className="font-medium text-purple-600 hover:text-purple-800">
                                        {Constants.HERO_FORM_PRIVACY_LINK_TEXT}
                                    </a>
                                </label>
                            </div>
                        </div>
                    </div>

                    <ButtonCustom variant="form" size="lg" fullWidth onClick={handleSubmit}>
                        {Constants.HERO_FORM_START_INTERVIEW_BUTTON_TEXT}
                    </ButtonCustom>
                </form>
            </GlassCard>
        </>
    );
};

export default InputForm;
